import {
  ArrowRight,
  BarChart3,
  Check,
  Cloud,
  FileText,
  Github,
  Layers,
  Puzzle,
  Server,
  Zap,
} from "lucide-react";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { SIGN_UP_LEDGER } from "@/config/product-links";
import { productUrl } from "@/lib/product-attribution";
import { useTranslation } from "@/common/providers/i18n-provider";
import { LocaleLink } from "@/components/locale-link";
import "@/styles/fava.css";

const FavaScreenshot1 = "https://beancount-io.b-cdn.net/fava-dashboard-1.png";
const FavaScreenshot2 = "https://beancount-io.b-cdn.net/fava-dashboard-2.webp";
const FavaScreenshot3 = "https://beancount-io.b-cdn.net/fava-dashboard-3.png";
const FAVA_SOURCE = "https://github.com/beancount/fava";

const FEATURES = [
  {
    icon: BarChart3,
    title: "fava.interactiveCharts",
    body: "fava.visualizeYourFinancialDataWithBeautiful",
  },
  {
    icon: Layers,
    title: "fava.comprehensiveReports",
    body: "fava.generateDetailedFinancialReportsIncludingBalance",
  },
  {
    icon: Zap,
    title: "fava.realTimeUpdates",
    body: "fava.yourBeancountFilesAreAutomaticallyMonitored",
  },
] as const;
const INTEGRATIONS = [
  {
    icon: FileText,
    title: "fava.fileBasedWorkflow",
    body: "fava.worksDirectlyWithYourExistingBeancount",
  },
  {
    icon: Layers,
    title: "fava.multiFileSupport",
    body: "fava.handleComplexLedgerStructuresWithMultiple",
  },
  {
    icon: Puzzle,
    title: "fava.pluginEcosystem",
    body: "fava.compatibleWithBeancountPluginsAndExtensions",
  },
] as const;
const SELF_HOSTED = [
  "fava.fullControlOverYourDataAnd",
  "fava.customizeConfigurationsAndPlugins",
  "fava.noMonthlyFeesOrUsageLimits",
  "fava.worksWithAnyBeancountFileStructure",
] as const;
const HOSTED = [
  "fava.zeroSetupReadyToUseImmediately",
  "fava.automaticBackupsAndSecurityUpdates",
  "fava.mobileAppIntegrationAndSync",
  "fava.aiPoweredInsightsAndAutomation",
  "fava.advancedCollaborationFeatures",
] as const;

export function FavaComponent() {
  const { t } = useTranslation();
  return (
    <div className="fava-page">
      <AppHeader />
      <main id="main">
        <section className="fava-hero">
          <div className="fava-container fava-hero-layout">
            <div className="fava-hero-copy">
              <h1>{t("fava.hostedFavaBeautifulBeancount")}</h1>
              <p>{t("fava.transformYourPlainTextAccountingInto")}</p>
              <div className="fava-actions">
                <a
                  className="fava-button fava-primary"
                  href={productUrl(SIGN_UP_LEDGER, "fava")}
                >
                  {t("common.getStartedFree")}
                  <ArrowRight size={18} aria-hidden="true" />
                </a>
                <a className="fava-button fava-secondary" href={FAVA_SOURCE}>
                  <Github size={18} aria-hidden="true" />
                  {t("common.viewSource")}
                </a>
              </div>
            </div>
            <figure className="fava-hero-preview">
              <img
                src={FavaScreenshot1}
                alt={t("fava.dashboardOverview")}
                width={1513}
                height={1121}
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
              <figcaption>
                <strong>{t("fava.dashboardOverview")}</strong>
                <span>{t("fava.getAComprehensiveViewOfYour")}</span>
              </figcaption>
            </figure>
          </div>
        </section>
        <div className="fava-container">
          <section
            className="fava-section fava-gallery"
            aria-labelledby="fava-gallery-title"
          >
            <div className="fava-section-heading">
              <h2 id="fava-gallery-title">{t("fava.seeFavaInAction")}</h2>
              <p>{t("fava.exploreTheBeautifulInterfaceAndPowerful")}</p>
            </div>
            <div className="fava-gallery-grid">
              <figure>
                <div className="fava-gallery-image">
                  <img
                    src={FavaScreenshot2}
                    alt={t("fava.interactiveReports")}
                    width={1095}
                    height={836}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <figcaption>
                  <h3>{t("fava.interactiveReports")}</h3>
                  <p>
                    {t("fava.generateDetailedFinancialReportsWithFiltering")}
                  </p>
                </figcaption>
              </figure>
              <figure>
                <div className="fava-gallery-image">
                  <img
                    src={FavaScreenshot3}
                    alt={t("fava.transactionManagement")}
                    width={1585}
                    height={1088}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <figcaption>
                  <h3>{t("fava.transactionManagement")}</h3>
                  <p>{t("fava.browseSearchAndManageYourTransactions")}</p>
                </figcaption>
              </figure>
            </div>
          </section>
          <section
            className="fava-section fava-features"
            aria-labelledby="fava-features-title"
          >
            <div className="fava-section-heading">
              <h2 id="fava-features-title">
                {t("fava.powerfulFeaturesForModernAccounting")}
              </h2>
              <p>{t("fava.favaBringsYourPlainTextAccounting")}</p>
            </div>
            <div className="fava-feature-grid">
              {FEATURES.map(({ icon: Icon, title, body }) => (
                <article key={title}>
                  <Icon size={26} aria-hidden="true" />
                  <h3>{t(title)}</h3>
                  <p>{t(body)}</p>
                </article>
              ))}
            </div>
          </section>
        </div>
        <section className="fava-hosting" aria-labelledby="fava-hosting-title">
          <div className="fava-container">
            <div className="fava-section-heading">
              <h2 id="fava-hosting-title">
                {t("fava.chooseYourFavaExperience")}
              </h2>
              <p>{t("fava.whetherYouPreferCompleteControlWith")}</p>
            </div>
            <div className="fava-hosting-grid">
              <article className="fava-self-hosted">
                <Server size={30} aria-hidden="true" />
                <h3>{t("fava.selfHostedFava")}</h3>
                <p>{t("fava.completeControlAndCustomization")}</p>
                <ul>
                  {SELF_HOSTED.map((key) => (
                    <li key={key}>
                      <Check size={18} aria-hidden="true" />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>
                <p className="fava-hosting-note">
                  {t("fava.requiresTechnicalSetupAndMaintenance")}
                </p>
                <a className="fava-button fava-secondary" href={FAVA_SOURCE}>
                  <Github size={18} aria-hidden="true" />
                  {t("fava.getStartedWithSelfHosting")}
                </a>
              </article>
              <article className="fava-managed">
                <Cloud size={30} aria-hidden="true" />
                <h3>{t("fava.beancountIoHostedFava")}</h3>
                <p>{t("fava.managedServiceWithPremiumFeatures")}</p>
                <ul>
                  {HOSTED.map((key) => (
                    <li key={key}>
                      <Check size={18} aria-hidden="true" />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>
                <div className="fava-hosting-note">
                  <strong>{t("fava.freeToStart")}</strong>
                  <LocaleLink to="/pricing">
                    {t("common.viewPricingPlans")}
                  </LocaleLink>
                </div>
                <a
                  className="fava-button fava-primary"
                  href={productUrl(SIGN_UP_LEDGER, "fava")}
                >
                  {t("common.getStartedFree")}
                  <ArrowRight size={18} aria-hidden="true" />
                </a>
              </article>
            </div>
          </div>
        </section>
        <div className="fava-container">
          <section
            className="fava-section fava-integration"
            aria-labelledby="fava-integration-title"
          >
            <div className="fava-section-heading">
              <h2 id="fava-integration-title">
                {t("fava.seamlessBeancountIntegration")}
              </h2>
              <p>{t("fava.favaWorksPerfectlyWithYourExisting")}</p>
            </div>
            <div className="fava-feature-grid">
              {INTEGRATIONS.map(({ icon: Icon, title, body }) => (
                <article key={title}>
                  <Icon size={26} aria-hidden="true" />
                  <h3>{t(title)}</h3>
                  <p>{t(body)}</p>
                </article>
              ))}
            </div>
          </section>
          <section className="fava-final">
            <h2>{t("common.readyToTransformYourAccounting")}</h2>
            <p>{t("fava.experienceThePowerOfFavaWith")}</p>
            <div className="fava-actions">
              <a
                className="fava-button fava-primary"
                href={productUrl(SIGN_UP_LEDGER, "fava")}
              >
                {t("common.getStartedFree")}
                <ArrowRight size={18} aria-hidden="true" />
              </a>
              <LocaleLink
                className="fava-button fava-secondary"
                to="/docs/introduction-to-beancount"
              >
                {t("common.learnMore")}
              </LocaleLink>
            </div>
          </section>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
